import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import PublicHeader from '../../Components/Header/PublicHeader';
import { fetchPublicPrivacyPolicy } from '../../Redux/PublicReservation/publicReservationApi';
import { PublicPrivacyPolicyView } from '../../types/reservation';
import { makeT, PublicReservationLang } from '../../i18n/publicReservationI18n';
import { LanguageToggle, PublicReservationFooter } from './shared';

/**
 * 店舗の「個人情報の取り扱い」公開ページ（認証不要・/reservations/:store_id/privacy）。
 * privacyPolicyType=TEXT の店舗が入力した本文を、その店舗の名前を見出しにして掲載する
 * （フィードバック3）。type=URL・未設定の店舗は BE が 404 を返すため案内文を表示する。
 * 本文は店舗入力の単一言語のまま表示し、見出し・案内のみ ja/en を切り替える。
 */
const PublicPrivacyPolicy: React.FC = () => {
  const { store_id: storeId } = useParams<{ store_id: string }>();
  const [lang, setLang] = useState<PublicReservationLang>('ja');
  const t = useMemo(() => makeT(lang), [lang]);

  const [policy, setPolicy] = useState<PublicPrivacyPolicyView | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchPublicPrivacyPolicy(storeId)
      .then((result) => {
        if (!cancelled) setPolicy(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Error && e.message.includes('404')) setNotFound(true);
        else setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  return (
    <Box>
      <PublicHeader />
      <Container maxWidth="sm" sx={{ mt: 3, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <LanguageToggle lang={lang} onChange={setLang} />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Box>
        ) : notFound ? (
          <Alert severity="info">{t('privacyPageNotFound')}</Alert>
        ) : failed ? (
          <Alert severity="error">{t('fetchError')}</Alert>
        ) : policy ? (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {`${policy.storeName}${t('privacyPageHeadingSuffix')}`}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {policy.text}
              </Typography>
            </Paper>
          </>
        ) : null}

        <PublicReservationFooter lang={lang} />
      </Container>
    </Box>
  );
};

export default PublicPrivacyPolicy;
